import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, ChevronDown } from "lucide-react";
import { useIsMobile } from "../../hooks/use-mobile";
import { useReportIds } from "../../hooks/useReportIds";
import Loading from "../Loading";
import { motion } from "framer-motion";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../Collapsible";
import { useTranslation } from "react-i18next";

const WellnessPlanningReport = () => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const { reportIds, isLoading } = useReportIds();
  const navigate = useNavigate();
  const { reportId } = useParams();
  const location = useLocation();
  const [currentReportId, setCurrentReportId] = useState<string | null>(reportId || null);
  const [reportData, setReportData] = useState<any>(null);
  const [error, setError] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [openStates, setOpenStates] = useState<Record<string, boolean>>({});
  
  // Check if we're on the start-report path (no specific report ID)
  const isStartReportPath = location.pathname.includes('/start-report/');

  useEffect(() => {
    if (isStartReportPath) {
      // For start-report path, use the INTAKE2 report ID if available
      if (reportIds.INTAKE2) {
        setCurrentReportId(reportIds.INTAKE2 as string);
      }
    } else if (!reportId && reportIds.INTAKE2) {
      // For other paths, use the reportId from params or fall back to INTAKE2
      setCurrentReportId(reportIds.INTAKE2 as string);
    } else if (reportId) {
      // If reportId is in URL, use it
      setCurrentReportId(reportId);
    }
  }, [reportId, reportIds.INTAKE2, isStartReportPath]);

  useEffect(() => {
    const fetchReportData = async () => {
      if (!currentReportId) return;
      try {
        setLoading(true);
        const response = await fetch(`${(import.meta as any).env.VITE_APP_URL}/report/intake/2/${currentReportId}`, {
          headers: {
            Accept: 'application/json',
          },
        });

        if (response.status === 500) {
          setError({ status: 500 });
          setLoading(false);
          return;
        }

        if (!response.ok) {
          setError({ status: response.status });
          setLoading(false);
          return;
        }

        const data = await response.json();
        setReportData(data);
        setError(null);
      } catch (err: any) {
        setError({ status: 'error', message: err.message });
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [currentReportId]); // Add currentReportId as a dependency

  if (isLoading || loading) {
    return <Loading className="h-screen" />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">{t("intakeReport.error.loading", "Error Loading Report")}</h1>
          <p className="text-gray-600 mb-6">
            {error.status === 404
              ? t("intakeReport.error.notFound", "Report not found. It may have been deleted or you don't have access.")
              : error.status === 500
                ? t("intakeReport.error.server", "Server error. Please try again later.")
                : t("intakeReport.error.unknown", "Error: {{message}}", { message: error.message || "Unknown error" })}
          </p>
          <Link to="/">
            <Button>{t("common.backToHome", "Return to Dashboard")}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const formatAreaTitle = (key: string): string => {
    switch (key) {
      case "sleep":
        return t("intakeReport.functionalAreas.sleep");
      case "socialSupport":
        return t("intakeReport.functionalAreas.socialSupport");
      case "energyLevel":
        return t("intakeReport.functionalAreas.energyLevel");
      case "stressManagement":
        return t("intakeReport.functionalAreas.stressManagement");
      case "cognitiveFunction":
        return t("intakeReport.functionalAreas.cognitiveFunction");
      case "emotionalRegulation":
        return t("intakeReport.functionalAreas.emotionalRegulation");
      default:
        return key.charAt(0).toUpperCase() + key.slice(1);
    }
  };

  // Define animations for card sections
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
  };

  // Handle toggling the open state for a specific area
  const toggleOpenState = (key: string) => {
    setOpenStates(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'en' ? 'en-US' : i18n.language === 'it' ? 'it-IT' : 'uk-UA', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-5xl">
      <div className="flex items-center mb-6">
          <Link to="/">
            <Button variant="ghost" className="-ml-2 pl-2 flex items-center gap-2 text-base">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">{t("intakeProcess.backToIntake", "Back to Intake Process")}</span>
              <span className="sm:hidden">{t("navigation.back")}</span>
            </Button>
          </Link>
        </div>
        {/* Header */}
        <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold">{t("intakeProcess.session.wellnessPlanning.title", "Health Assessment Report")}</h1>
            <p className="mt-2">{formatDate(reportData?.userInfo?.date)} • {t("intakeProcess.session.number", "Session {{number}}", { number: reportData?.userInfo?.callNumber })}</p>
          <p className="text-gray-600">
            {reportData?.userInfo?.name} • {reportData?.userInfo?.email}
          </p>
        </div>
    
        {/* Functional Areas */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-8"
        >
          <div className="bg-[#006C5B] text-white p-4 rounded-md mb-4">
            <div className="text-xl font-medium">{t("intakeReport.sections.functionalAreas")}</div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {reportData?.functionalAreas.map((area) => (
              <Card key={area.key} className="shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <CardTitle>{formatAreaTitle(area.key)}</CardTitle>
                </CardHeader>
                
                <CardContent>
                  <ul className="list-disc pl-5 mb-4">
                    {area?.observations.map((observation, index) => (
                      <li key={index} className="mb-2 text-gray-700">{observation}</li>
                    ))}
                  </ul>
                  
                  <Collapsible 
                    open={openStates[area.key] || false} 
                    onOpenChange={() => toggleOpenState(area.key)}
                  >
                    <CollapsibleTrigger className="flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors">
                      {openStates[area.key] ? t("intakeReport.details.hideDetails") : t("intakeReport.details.showDetails")}
                      <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${openStates[area.key] ? 'rotate-180' : ''}`} />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pt-2 text-gray-600 italic">
                      "{area?.evidence}"
                    </CollapsibleContent>
                  </Collapsible>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.section>
        
        {/* Emerging Patterns */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mb-8"
        >
          <div className="bg-[#006C5B] text-white p-4 rounded-md mb-4">
            <div className="text-xl font-medium">{t("intakeReport.sections.emergingPatterns", "Emerging Patterns")}</div>
          </div>
          
          {reportData?.emergingPatterns.map((pattern, index) => (
            <Card key={index} className="mb-4 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{pattern?.pattern}</CardTitle>
              </CardHeader>
              
              <CardContent>
                <p className="mb-4 text-gray-700">{pattern?.significance}</p>
                
                <div className="flex flex-wrap gap-2">
                  {pattern?.relatedAreas.map((area, i) => (
                    <span key={i} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                      {formatAreaTitle(area)}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </motion.section>
        
        {/* Next Steps */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="bg-[#006C5B] text-white p-4 rounded-md mb-4">
            <div className="text-xl font-medium">{t("intakeReport.sections.nextSteps")}</div>
          </div>
          
          <Card className="shadow-sm">
            <CardContent className="pt-6">
              <p className="mb-4 text-gray-700">{t("intakeReport.nextSteps.introduction", "In our next conversation, we'll explore:")}</p>
              
              <ul className="list-disc pl-5">
                {reportData?.nextSteps?.explorationQuestions.map((question, index) => (
                  <li key={index} className="mb-3 text-gray-700">{question}</li>
                ))}
              </ul>
              
              {/* Continue Button */}
              <div className="mt-8 flex justify-center">
                <Link to={reportIds.INTAKE3 || reportIds.INTAKE ? reportIds.INTAKE ? `/auth/report/${reportIds.INTAKE3}/intake3` : '/auth/start-report/intake3' : '/auth/start-report/#action-plan'}>
                  <Button className="bg-primary hover:bg-primary/90">
                    {reportIds.INTAKE3 
                      ? t("intakeProcess.buttons.viewNextReport", "View Action Plan Report") 
                      : t("intakeProcess.buttons.continueToNextSession", "Continue to Action Plan Session")}
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.section>
      </div>
    </div>
  );
};

export default WellnessPlanningReport;
